import React, { useState } from "react";

import type { ChatAccess } from "./useChatAccess";
import {
  M365_COPILOT_URL,
  PRIVACY_TEAM_URL,
  TEAMS_UPDATES_THREAD_URL,
} from "./accessLinks";
import chatStyles from "styles/chat.module.scss";
import styles from "./ChatAccessNotice.module.scss";

const ACCESS_CUTOFF_DATE = "November 2, 2026";
const ACCESS_REQUEST_URL =
  "https://agricompany.identitynow.com/ui/d/mysailpoint";

/** What a MyAccess request for the chat group should include. */
const ACCESS_REQUEST_ITEMS = [
  {
    label: "Role and team",
    detail: "and whether analysis is a core job function or occasional",
  },
  {
    label: "Specific use cases",
    detail:
      "2-3 recurring uses, not general statements, and the specific Vylor Research Chat functionality used",
  },
  {
    label: "Why Copilot is insufficient",
    detail: "name the specific capability gap",
  },
  {
    label: "Data types and classification",
    detail: "what data will be processed, and its sensitivity tier",
  },
  {
    label: "Expected frequency",
    detail: "daily, weekly, or project-based",
  },
  {
    label: "Business outcome",
    detail:
      "cycle time reduction, reduced dependency on a specific analyst, or improved reproducibility",
  },
];

interface ChatAccessNoticeProps {
  access: Extract<ChatAccess, "warned" | "denied" | "unavailable">;
  /** Shown below the notice, e.g. a button into the app. */
  children?: React.ReactNode;
}

/** Tells a user outside the chat group about losing access (ADR 076). */
export default function ChatAccessNotice({
  access,
  children,
}: ChatAccessNoticeProps) {
  const [requestOpen, setRequestOpen] = useState(false);

  if (access === "unavailable") {
    return (
      <div className={styles.notice} role="status">
        <p className={styles.title}>We couldn't check your chat access</p>
        <p className={styles.body}>Reload the page to try again.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className={`${chatStyles.actionButtonPrimary} ${styles.reloadButton}`}
        >
          Reload page
        </button>
      </div>
    );
  }

  return (
    <div className={styles.notice} role="status">
      <p className={styles.title}>
        {access === "warned"
          ? `Research Chat access will be discontinued for your account on ${ACCESS_CUTOFF_DATE}`
          : "Research Chat access has been discontinued for your account"}
      </p>
      <p className={styles.body}>
        Access is limited to R&amp;D and GSO users as per the decision made by
        the{" "}
        <a href={PRIVACY_TEAM_URL} target="_blank" rel="noopener noreferrer">
          Vylor Privacy team
        </a>
        . Please use{" "}
        <a href={M365_COPILOT_URL} target="_blank" rel="noopener noreferrer">
          M365 Copilot
        </a>{" "}
        instead. For updates, see{" "}
        <a
          href={TEAMS_UPDATES_THREAD_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          this thread on MS Teams
        </a>
        .
      </p>
      {access === "warned" && (
        <p className={styles.body}>
          Export any chats you want to retain before {ACCESS_CUTOFF_DATE}.
        </p>
      )}
      <button
        type="button"
        className={styles.requestToggle}
        onClick={() => setRequestOpen(!requestOpen)}
        aria-expanded={requestOpen}
      >
        <span className={styles.toggleArrow}>{requestOpen ? "▼" : "▶"}</span>
        How to request access
      </button>
      {requestOpen && (
        <div className={styles.requestDetails}>
          <p className={styles.body}>
            If you believe you need access, request access to the{" "}
            <b>VylorResearchChatGlobalUsers</b> group in{" "}
            <a
              href={ACCESS_REQUEST_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              MyAccess
            </a>{" "}
            with the following information:
          </p>
          <ol className={styles.requestList}>
            {ACCESS_REQUEST_ITEMS.map((item) => (
              <li key={item.label}>
                <b>{item.label}</b>: {item.detail}
              </li>
            ))}
          </ol>
        </div>
      )}
      {children}
    </div>
  );
}
